import React, { useEffect, useState } from 'react';
import "./PopeyeShipmentMap.scss";
import "leaflet/dist/leaflet.css";
import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import { worldSeaRegions } from '../../utils/map';
import ShipmentMapPopup from './ShipmentMapPopup/ShipmentMapPopup';
import { getMarkerIcons } from '../../utils/images';

const ZOOM_LEVEL = 6;
const OUT_ZOOM_LEVEL = 3;
const pointSwitchDelay = 3000;  // Delay before switching to the next region
const zoomPauseDelay = 2000;    // Delay between zoom-in and zoom-out

const RegionZoomController = ({ regions, currentRegionIndex, setCurrentRegionIndex }) => {
  const map = useMap();
  const [isZooming, setIsZooming] = useState(false);

  const nonEmptyRegions = Object.entries(regions).filter(
    ([key, shipments]) => shipments.length > 0
  );
  const regionKeys = nonEmptyRegions.map(([key]) => key);
  const regionKey = regionKeys[currentRegionIndex];
  const regionShipments = regionKey ? regions[regionKey] : [];

  const zoomToBounds = (bounds, zoomLevel, delay) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        map.fitBounds(bounds, { maxZoom: zoomLevel });
        resolve();
      }, delay);
    });
  };

  const zoomSequence = async (bounds) => {
    setIsZooming(true);

    // Zoom in with three steps
    await zoomToBounds(bounds, ZOOM_LEVEL - 2, 0);    // Step 1 zoom-in
    await zoomToBounds(bounds, ZOOM_LEVEL - 1, 1500); // Step 2 zoom-in
    await zoomToBounds(bounds, ZOOM_LEVEL, 1500);     // Step 3 zoom-in

    // Pause at max zoom level
    await new Promise((resolve) => setTimeout(resolve, zoomPauseDelay));

    // Zoom out in three steps
    await zoomToBounds(bounds, ZOOM_LEVEL - 1, 1500); // Step 1 zoom-out
    await zoomToBounds(bounds, ZOOM_LEVEL - 2, 1500); // Step 2 zoom-out
    await zoomToBounds(bounds, OUT_ZOOM_LEVEL, 1500); // Step 3 zoom-out
    await new Promise((resolve) => setTimeout(resolve, pointSwitchDelay));

    setIsZooming(false);
  };

  const performZoomSequence = async () => {
    if (regionShipments.length > 0) {
      const validShipments = regionShipments.filter((s) => s.lat !== null && s.lng !== null);

      if (validShipments.length > 0) {
        const bounds = L.latLngBounds(validShipments.map((s) => [s.lat, s.lng]));

        await zoomSequence(bounds);

        // Add an extra delay before switching to the next region
        await new Promise((resolve) => setTimeout(resolve, pointSwitchDelay));

        // Move to the next region only if no other zoom sequence is in progress
        if (!isZooming) {
          setCurrentRegionIndex((prevIndex) => (prevIndex + 1) % regionKeys.length);
        }
      }
    }
  };

  useEffect(() => {
    if (!isZooming) {
      performZoomSequence();
    }
  }, [currentRegionIndex, regionShipments, isZooming]);

  return null;
};

const PopeyeShipmentMap = ({ shipments, selectShipment }) => {
  const [currentRegionIndex, setCurrentRegionIndex] = useState(0);
  const REGION_BOUNDS = worldSeaRegions();

  // Group shipments by region, including only regions with shipments
  const regions = Object.keys(REGION_BOUNDS).reduce((acc, regionKey) => {
    const { latRange, lngRange } = REGION_BOUNDS[regionKey];
    const filteredShipments = shipments.filter(
      (shipment) =>
        shipment.lat !== null &&
        shipment.lng !== null &&
        shipment.lat >= latRange[0] &&
        shipment.lat <= latRange[1] &&
        shipment.lng >= lngRange[0] &&
        shipment.lng <= lngRange[1]
    );

    if (filteredShipments.length > 0) {
      acc[regionKey] = filteredShipments;
    }
    return acc;
  }, {});

  return (
    <div className="PopeyeShipmentMap">
      <MapContainer center={[0, 0]} zoom={OUT_ZOOM_LEVEL} scrollWheelZoom={false} style={{ height: 'inherit' }}>
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='Map data © <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        {shipments
          .filter((shipment) => shipment.lat !== null && shipment.lng !== null)
          .map((shipment, index) => {
            const fileName =
              (shipment.state === "delivered" || shipment.state === "pod" || shipment.state === "customs")
                ? "delivered"
                : (shipment.state === "sea" || shipment.state === "pol")
                  ? (shipment.delay === undefined ? "sailing" : "delayed")
                  : "scheduled";
            return (
              <Marker
                key={index}
                position={[shipment.lat, shipment.lng]}
                icon={L.icon({
                  iconUrl: getMarkerIcons(`${fileName}-location`),
                  iconSize: [58, 58],
                  iconAnchor: [15, 15],
                  popupAnchor: [-3, -10]
                })}
              >
                <Popup position="right" autoPan={false} closeButton={false}>
                  <ShipmentMapPopup
                    name={"shipment.pol.name"}
                    shipment={shipment}
                    selectShipment={selectShipment}
                  />
                </Popup>
              </Marker>
            );
          })}
        <RegionZoomController
          regions={regions}
          currentRegionIndex={currentRegionIndex}
          setCurrentRegionIndex={setCurrentRegionIndex}
        />
      </MapContainer>
    </div>
  );
};

export default PopeyeShipmentMap;
